<script setup lang="ts">
import { computed, Ref, ref } from 'vue'
import { Projection } from 'mapbox-gl'
import MapContainer from '@/components/MapContainer.vue'
import { Duration } from 'luxon'
import { roundTo } from '@/utils/Math'
import { restrictToNumbers } from '@/utils/restrictKeyPress'

const mapProjectType: Projection = { name: 'equirectangular' } as Projection
const accessToken: string = 'pk.eyJ1IjoiYW5rb3ItaW8iLCJhIjoiY2wwbTM5a2g3MHF1dDNjbzNiaXF1NDRvNiJ9.GZeEMiSzCV3PesOXPBqMhQ'
const mapBoxStyle: string = 'mapbox://styles/ankor-io/cl7jmtqs9000314qm7uyc31m7'
const currencyOptions = [
  { label: 'USD', value: 'USD', name: 'US dollar', symbol: 'US$' },
  { label: 'EUR', value: 'EUR', name: 'Euro', symbol: '€' },
  { label: 'GBP', value: 'GBP', name: 'British pound', symbol: '£' },
  { label: 'AUD', value: 'AUD', name: 'Australian dollar', symbol: 'A$' },
  { label: 'AED', value: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
  { label: 'SGD', value: 'SGD', name: 'Singapore dollar', symbol: 'S$' },
  { label: 'HKD', value: 'HKD', name: 'Hong Kong dollar', symbol: 'HK$' },
  { label: 'JPY', value: 'JPY', name: 'Japanese yen', symbol: '¥' },
  { label: 'CAD', value: 'CAD', name: 'Canadian dollar', symbol: 'C$' },
  { label: 'CHF', value: 'CHF', name: 'Swiss franc', symbol: '₣' },
  // { label: 'BTC', value: 'BTC', name: 'Bitcoin', symbol: '₿' },
  // { label: 'ETH', value: 'ETH', name: 'Ether', symbol: 'Ξ' },
]

const distanceRef: Ref<number> = ref(0)
const distanceUnitRef: Ref<'km' | 'nm'> = ref('nm')
const speedRef: Ref<number> = ref(0)
const speedUnitRef: Ref<'kn' | 'kph'> = ref('kn')
const fuelConsumptionRef: Ref<number> = ref(0)
const fuelConsumptionUnitRef: Ref<'l/h' | 'gal/h'> = ref('l/h')
const fuelCostRef: Ref<number> = ref(0)
const fuelCostUnitRef: Ref<'l' | 'gal'> = ref('l')
const selectedCurrencyRef = ref('USD')
const imageList = ref([10, 12, 16, 29, 35, 43, 45, 49, 57, 74])

const getRandom = () => imageList.value[Math.floor(Math.random() * imageList.value.length)]

const timeTaken = computed(() => {
  if (speedRef.value === 0 || distanceRef.value === 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
    }
  }
  // Calculate time taken to travel the distance
  const distanceInNm = distanceUnitRef.value === 'nm' ? distanceRef.value : distanceRef.value / 1.852
  const speedInKnots = speedUnitRef.value === 'kn' ? speedRef.value : speedRef.value / 1.852
  const timeTakenInHours = distanceInNm / speedInKnots
  const duration = Duration.fromObject({ hours: timeTakenInHours }).rescale().toFormat('d::h::m')
  const [days, hours, minutes] = duration.split('::')

  return {
    days,
    hours,
    minutes,
  }
})

const getSymbol = (currency: string) => {
  return currencyOptions.find((option) => option.value === currency)?.symbol
}

const totalFuelUsed = computed(() => {
  if (speedRef.value === 0 || distanceRef.value === 0) {
    return 0
  }
  return `${roundTo((distanceRef.value / speedRef.value) * fuelConsumptionRef.value, 0)} ${
    fuelConsumptionUnitRef.value === 'l/h' ? 'Litres' : 'Gallons'
  }`
})

const totalCost = computed(() => {
  if (speedRef.value === 0 || distanceRef.value === 0) {
    return 0
  }
  // calculate the total fuel used
  return `${getSymbol(selectedCurrencyRef.value)} ${roundTo(
    (distanceRef.value / speedRef.value) * fuelConsumptionRef.value * fuelCostRef.value,
    0,
  )}`
})

const updateDistance = (distanceInKms: number) => {
  if (distanceUnitRef.value === 'km') {
    distanceRef.value = roundTo(distanceInKms)
  } else {
    distanceRef.value = roundTo(distanceInKms * 0.539957)
  }
}

const handleClear = () => {
  distanceRef.value = 0
  distanceUnitRef.value = 'km'
  speedRef.value = 0
  speedUnitRef.value = 'kn'
  fuelConsumptionRef.value = 0
  fuelConsumptionUnitRef.value = 'l/h'
  fuelCostRef.value = 0
  fuelCostUnitRef.value = 'l'
}
</script>
<template>
  <section class="flex flex-col items-center gap-y-6">
    <!-- heading -->
    <h2 class="hidden text-4xl leading-none text-purple-800 text-center font-extrabold">FUEL CALCULATOR</h2>
    <!-- banner -->
    <div class="hidden w-3/4 max-w-5xl h-16 md:h-24 rounded-xl overflow-hidden transition-all">
      <div
        class="w-full h-full flex items-center justify-center bg-no-repeat p-4 border border-gray-200 border-r-0 rounded-r-none shadow-sm"
        :style="{
          backgroundImage: `url('https://picsum.photos/id/${getRandom()}/1024/400')`,
        }"
      >
        <p
          class="hidden md:block p-1 md:p-2.5 border-2 sm:border-4 rounded bg-black/40 text-white font-normal md:font-bold text-xs md:text-xl lg:text-2xl transition-all"
        >
          Share bespoke and ready-to-go itineraries in minutes
        </p>
        <p
          class="block md:hidden p-1 md:p-2.5 border-2 sm:border-4 rounded bg-black/40 text-white font-normal md:font-bold text-xs md:text-xl lg:text-2xl transition-all"
        >
          Share stunning itineraries in minutes
        </p>
      </div>
      <a
        href="https://ankor.io/itineraries"
        class="flex justify-center items-center px-3 text-base font-medium text-center text-white bg-orange-400 hover:bg-orange-500"
      >
        <svg
          class="w-8 h-8"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="3"
          stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
        </svg>
      </a>
    </div>
    <!-- calc -->
    <div class="pb-2 w-full flex flex-col gap-y-4">
      <!-- map and calc inputs -->
      <div class="md:grid md:grid-cols-3">
        <MapContainer
          class="md:col-span-2"
          container="fuel-calculator-map"
          :style="mapBoxStyle"
          :access-token="accessToken"
          :projection-type="mapProjectType"
          @update:distance="updateDistance"
          @clear:complete="handleClear"
        ></MapContainer>
        <!-- calc inputs -->
        <div class="flex justify-center items-center p-3">
          <div class="bg-white flex flex-col gap-y-6 p-5 rounded-xl">
            <!-- Distance -->
            <div class="flex flex-col items-center">
              <label for="default-input-distance" class="block text-sm text-purple-600 font-semibold"> DISTANCE </label>
              <div class="w-full flex items-center gap-x-2">
                <input
                  type="number"
                  id="default-input-distance"
                  class="m-2 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  v-model="distanceRef"
                  pattern="^\d+(?:\.\d{1,2})?$"
                  @keydown.up.prevent
                  @keydown.down.prevent
                  @keypress="restrictToNumbers($event)"
                />
                <div class="flex flex-col justify-center w-full h-full">
                  <div class="flex items-center me-4">
                    <input
                      id="inline-checked-radio-distance-nm"
                      type="radio"
                      name="inline-radio-group-distance"
                      :checked="distanceUnitRef === 'nm'"
                      class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      @click="
                        () => {
                          if (distanceUnitRef === 'nm') {
                            return
                          }
                          distanceUnitRef = 'nm'
                          distanceRef = roundTo(distanceRef * 0.539957)
                        }
                      "
                    />
                    <label
                      for="inline-checked-radio-distance-nm"
                      class="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      Nautical Miles
                    </label>
                  </div>
                  <div class="flex items-center me-4">
                    <input
                      id="inline-radio-distance-km"
                      type="radio"
                      name="inline-radio-group-distance"
                      :checked="distanceUnitRef === 'km'"
                      class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      @click="
                        () => {
                          if (distanceUnitRef === 'km') {
                            return
                          }
                          distanceUnitRef = 'km'
                          distanceRef = roundTo(distanceRef / 0.539957)
                        }
                      "
                    />
                    <label
                      for="inline-radio-distance-km"
                      class="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      Kilometers
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Speed -->
            <div class="flex flex-col items-center">
              <label for="default-input-speed" class="block text-sm text-purple-600 font-semibold"> SPEED </label>
              <div class="w-full flex items-center gap-x-2">
                <!-- number input with 2 decimal places -->
                <input
                  type="number"
                  id="default-input-speed"
                  class="m-2 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  v-model="speedRef"
                  pattern="^\d+(?:\.\d{1,2})?$"
                  @keydown.up.prevent
                  @keydown.down.prevent
                  @keypress="restrictToNumbers($event)"
                />
                <div class="flex flex-col justify-center w-full h-full">
                  <div class="flex items-center me-4">
                    <input
                      id="inline-radio-speed-kn"
                      type="radio"
                      name="inline-radio-group-speed"
                      :checked="speedUnitRef === 'kn'"
                      class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      @click="
                        () => {
                          if (speedUnitRef === 'kn') {
                            return
                          }
                          speedUnitRef = 'kn'
                          speedRef = roundTo(speedRef / 1.852)
                        }
                      "
                    />
                    <label
                      for="inline-radio-speed-kn"
                      class="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      Knots
                    </label>
                  </div>
                  <div class="flex items-center me-4">
                    <input
                      id="inline-checked-radio-speed-kph"
                      type="radio"
                      name="inline-radio-group-speed"
                      :checked="speedUnitRef === 'kph'"
                      class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      @click="
                        () => {
                          if (speedUnitRef === 'kph') {
                            return
                          }
                          speedUnitRef = 'kph'
                          speedRef = roundTo(speedRef * 1.852)
                        }
                      "
                    />
                    <label
                      for="inline-checked-radio-speed-kph"
                      class="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      Km / hr
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Fuel consumption -->
            <div class="flex flex-col items-center">
              <label for="default-input-fuel-consumption" class="block text-sm text-purple-600 font-semibold">
                FUEL CONSUMPTION
              </label>
              <div class="w-full flex items-center gap-x-2">
                <input
                  type="number"
                  id="default-input-fuel-consumption"
                  class="m-2 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  v-model="fuelConsumptionRef"
                  pattern="^\d+(?:\.\d{1,2})?$"
                  @keydown.up.prevent
                  @keydown.down.prevent
                  @keypress="restrictToNumbers($event)"
                />
                <div class="flex flex-col justify-center w-full h-full">
                  <div class="flex items-center me-4">
                    <input
                      id="inline-radio-fuel-consumption-lph"
                      type="radio"
                      name="inline-radio-fuel-consumption"
                      :checked="fuelConsumptionUnitRef === 'l/h'"
                      class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      @click="
                        () => {
                          if (fuelConsumptionUnitRef === 'l/h') {
                            return
                          }
                          fuelConsumptionUnitRef = 'l/h'
                          fuelConsumptionRef = roundTo(fuelConsumptionRef / 0.264172)
                        }
                      "
                    />
                    <label
                      for="inline-radio-fuel-consumption-lph"
                      class="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      Litres / hr
                    </label>
                  </div>
                  <div class="flex items-center me-4">
                    <input
                      id="inline-radio-fuel-consumption-gph"
                      type="radio"
                      name="inline-radio-fuel-consumption"
                      :checked="fuelConsumptionUnitRef === 'gal/h'"
                      class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      @click="
                        () => {
                          if (fuelConsumptionUnitRef === 'gal/h') {
                            return
                          }
                          fuelConsumptionUnitRef = 'gal/h'
                          fuelConsumptionRef = roundTo(fuelConsumptionRef * 0.264172)
                        }
                      "
                    />
                    <label
                      for="inline-radio-fuel-consumption-gph"
                      class="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      Gallons / hr
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Fuel cost -->
            <div class="flex flex-col items-center">
              <label for="default-input-fuel-cost" class="block text-sm text-purple-600 font-semibold">
                FUEL COST
              </label>
              <div class="ml-2 w-full flex items-center">
                <select
                  class="w-fit bg-blend-difference text-gray-500 bg-gray-50 border border-gray-300 focus:ring-2 focus:outline-none focus:ring-blue-300 rounded-md"
                  id="currency-select"
                  @change="selectedCurrencyRef = ($event.target as HTMLSelectElement).value"
                >
                  <option
                    v-for="option of currencyOptions"
                    :key="`${option.value}`"
                    :value="option.value"
                    :selected="selectedCurrencyRef === option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <input
                  type="number"
                  id="default-input-fuel-cost"
                  class="m-2 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  v-model="fuelCostRef"
                  pattern="^\d+(?:\.\d{1,2})?$"
                  @keydown.up.prevent
                  @keydown.down.prevent
                  @keypress="restrictToNumbers($event)"
                />
                <div class="flex flex-col justify-center w-full h-full">
                  <div class="flex items-center me-4">
                    <input
                      id="inline-radio-fuel-cost-l"
                      type="radio"
                      name="inline-radio-fuel-cost"
                      :checked="fuelCostUnitRef === 'l'"
                      class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      @click="
                        () => {
                          if (fuelCostUnitRef === 'l') {
                            return
                          }
                          fuelCostUnitRef = 'l'
                          fuelCostRef = roundTo(fuelCostRef / 0.264172)
                        }
                      "
                    />
                    <label
                      for="inline-radio-fuel-cost-l"
                      class="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300 text-nowrap"
                    >
                      Per Litre
                    </label>
                  </div>
                  <div class="flex items-center me-4">
                    <input
                      id="inline-radio-fuel-cost-gal"
                      type="radio"
                      name="inline-radio-fuel-cost"
                      :checked="fuelCostUnitRef === 'gal'"
                      class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      @click="
                        () => {
                          if (fuelCostUnitRef === 'gal') {
                            return
                          }
                          fuelCostUnitRef = 'gal'
                          fuelCostRef = roundTo(fuelCostRef * 0.264172)
                        }
                      "
                    />
                    <label
                      for="inline-radio-fuel-consumption-gal"
                      class="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300 text-nowrap"
                    >
                      Per Gallon
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Time taken and total cost -->
      <div class="mx-auto text-white border-2 border-gray-200 rounded-lg h-48 md:h-24">
        <div class="flex flex-col md:flex-row justify-center sm:gap-x-2 gap-y-2 p-2">
          <div>
            <h2 class="text-xl md:text-2xl font-bold text-center">TIME TAKEN</h2>
            <dl class="flex gap-8">
              <div class="flex flex-col items-center justify-center">
                <dt class="text-xl md:text-2xl font-extrabold">{{ timeTaken.days }}</dt>
                <dd class="text-xs uppercase">Days</dd>
              </div>
              <div class="flex flex-col items-center justify-center">
                <dt class="text-xl md:text-2xl font-extrabold">{{ timeTaken.hours }}</dt>
                <dd class="text-xs uppercase">Hours</dd>
              </div>
              <div class="flex flex-col items-center justify-center">
                <dt class="text-xl md:text-2xl font-extrabold">{{ timeTaken.minutes }}</dt>
                <dd class="text-xs uppercase">Minutes</dd>
              </div>
            </dl>
          </div>
          <div class="mx-2 md:my-1 border border-l border-solid bg-gray-400"></div>
          <section>
            <h2 class="text-xl md:text-2xl font-bold text-center">TOTAL</h2>
            <dl class="flex gap-8">
              <div class="flex flex-col items-center justify-center">
                <dt class="text-xl md:text-2xl font-extrabold">{{ totalFuelUsed }}</dt>
                <dd class="uppercase text-xs">Fuel Consumed</dd>
              </div>
              <div class="flex flex-col items-center justify-center">
                <dt class="text-xl md:text-2xl font-extrabold">{{ totalCost }}</dt>
                <dd class="uppercase text-xs">Total Cost</dd>
              </div>
            </dl>
          </section>
        </div>
      </div>

      <!-- text steps -->
      <div class="hidden mx-auto flex flex-col gap-y-8 px-8 max-w-5xl h-[700px] md:h-[555px] overflow-auto">
        <div class="flex flex-col">
          <p class="text-white mb-3 text-lg">Step 1. Plan your route</p>
          <p class="text-white">
            Click on the map to add waypoints (stops) for your trip or start by searching for the location. As you
            click, the distance table will automatically update to show the total distance between each point. If you
            have made a mistake unclick the waypoint to go back or clear route in the bottom left hand corner to start
            from the beginning.
          </p>
        </div>
        <div class="flex flex-col">
          <p class="text-white mb-3 text-lg">Step 2. Input your vessel details</p>
          <p class="text-white">Enter the following information</p>
          <ul class="list-disc list-inside text-white">
            <li>Speed: Your average cruising speed for the vessel.</li>
            <li>Fuel Consumption: The fuel consumption of your vessel (in litres or gallons per hour)</li>
            <li>Fuel Cost: The current price of fuel per litre or per gallon.</li>
          </ul>
        </div>
        <div class="flex flex-col">
          <p class="text-white mb-3 text-lg">Step 3. Calculate your fuel costs</p>
          <p class="text-white">
            Once you have entered all the details, the calculator will automatically estimate the total cost for your
            trip. Share the totals via email for reference.
          </p>
        </div>
        <div class="flex flex-col pb-8">
          <p class="text-white mb-3 text-lg">Need Some Assistance?</p>
          <p class="text-white">
            Leave your contact details via the form below or contact you existing Customer Success member for further
            yacht or itinerary suggestions.
          </p>
        </div>
      </div>

      <div class="hidden outer-triangle"></div>
    </div>
  </section>
</template>
<style lang="scss" scoped>
.outer-triangle {
  width: 100%;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    padding-bottom: 4%;
    background: rgb(35 56 118);
    clip-path: polygon(0% 0%, 100% 0%, 50% 100%);
  }
}
</style>
